import memoize from 'lodash/memoize';
import { SheetMap } from 'records/Sheet';
import { SheetQuery } from 'records/SheetQuery';
import { SheetVersion } from 'records/SheetVersion';
import { Discipline } from 'records/Discipline';

interface SortHandlers {
  [key: string]: (sheets: SheetMap, direction: string) => SheetMap;
}

const sortHandlers: SortHandlers = {
  originalOrder: (sheets: SheetMap, direction): SheetMap => {
    const sortedSheets = sheets.sort((sheetA, sheetB): number => {
      let a =
        direction === 'ascending'
          ? sheetA.versions.last(null)?.documentTitle || 0
          : sheetB.versions.last(null)?.documentTitle || 0;
      let b =
        direction === 'ascending'
          ? sheetB.versions.last(null)?.documentTitle || 0
          : sheetA.versions.last(null)?.documentTitle || 0;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      a =
        direction === 'ascending'
          ? sheetA.versions.last(null)?.pageIndex || 0
          : sheetB.versions.last(null)?.pageIndex || 0;
      b =
        direction === 'ascending'
          ? sheetB.versions.last(null)?.pageIndex || 0
          : sheetA.versions.last(null)?.pageIndex || 0;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedSheets;
  },

  publishedDate: (sheets: SheetMap, direction): SheetMap => {
    const sortedSheets = sheets.sort((sheetA, sheetB): number => {
      const a =
        direction === 'ascending'
          ? sheetA.versions.last(null)?.publishedtimestamp || 0
          : sheetB.versions.last(null)?.publishedtimestamp || 0;
      const b =
        direction === 'ascending'
          ? sheetB.versions.last(null)?.publishedtimestamp || 0
          : sheetA.versions.last(null)?.publishedtimestamp || 0;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedSheets;
  },

  uploadedDate: (sheets: SheetMap, direction): SheetMap => {
    const sortedSheets = sheets.sort((sheetA, sheetB): number => {
      const a =
        direction === 'ascending'
          ? sheetA.versions.last(null)?.uploadtimestamp || 0
          : sheetB.versions.last(null)?.uploadtimestamp || 0;
      const b =
        direction === 'ascending'
          ? sheetB.versions.last(null)?.uploadtimestamp || 0
          : sheetA.versions.last(null)?.uploadtimestamp || 0;

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedSheets;
  },

  title: (sheets: SheetMap, direction): SheetMap => {
    const sortedSheets = sheets.sort((sheetA, sheetB): number => {
      const a =
        direction === 'ascending'
          ? sheetA.versions.last(null)?.sheettitle.toLowerCase() || ''
          : sheetB.versions.last(null)?.sheettitle.toLowerCase() || '';
      const b =
        direction === 'ascending'
          ? sheetB.versions.last(null)?.sheettitle.toLowerCase() || ''
          : sheetA.versions.last(null)?.sheettitle.toLowerCase() || '';

      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }

      return 0;
    });

    return sortedSheets;
  },

  number: (sheets: SheetMap, direction): SheetMap => {
    const collator = new Intl.Collator(undefined, { numeric: true });
    const sortedSheets = sheets.sort((sheetA, sheetB): number => {
      const sheetAVersion = sheetA.versions.last(null)?.sheetnumber || '';
      const sheetBVersion = sheetB.versions.last(null)?.sheetnumber || '';
      return direction === 'ascending'
        ? collator.compare(sheetAVersion, sheetBVersion)
        : collator.compare(sheetBVersion, sheetAVersion);
    });

    return sortedSheets;
  },

  discipline: (sheets: SheetMap, direction): SheetMap => {
    const sortedSheets = sheets.sort((sheetA, sheetB): number => {
      const a =
        direction === 'ascending'
          ? sheetA.versions
              .last(new SheetVersion())
              .get('sheetDiscipline', new Discipline())
          : sheetB.versions
              .last(new SheetVersion())
              .get('sheetDiscipline', new Discipline());
      const b =
        direction === 'ascending'
          ? sheetB.versions
              .last(new SheetVersion())
              .get('sheetDiscipline', new Discipline())
          : sheetA.versions
              .last(new SheetVersion())
              .get('sheetDiscipline', new Discipline());

      if (a.get('sortOrder') > b.get('sortOrder')) {
        return 1;
      }

      if (b.get('sortOrder') > a.get('sortOrder')) {
        return -1;
      }

      return 0;
    });

    return sortedSheets;
  },
};

function sortSheets(sheets: SheetMap, sheetQuery: SheetQuery): SheetMap {
  const { sortField, sortDirection } = sheetQuery;

  if (sortHandlers[sortField]) {
    sheets = sortHandlers[sortField](sheets, sortDirection);
  }

  return sheets;
}

function searchSheets(sheets: SheetMap, sheetQuery: SheetQuery): SheetMap {
  const { search } = sheetQuery;

  if (search.length === 0) {
    return sheets;
  }

  const sanitizeString = (sheetNumber: string) =>
    sheetNumber.toLowerCase().replaceAll(/[.-\s]+/g, '');

  const sanitizedSearch = sanitizeString(search);

  const results = sheets.filter((sheet) => {
    const lastSheetVersion = sheet.versions.last<SheetVersion>();

    const sanitizedSheetNumber = sanitizeString(lastSheetVersion.sheetnumber);

    if (sanitizedSheetNumber.indexOf(sanitizedSearch) === 0) {
      return true;
    }

    const sanitizedSheetTitle = sanitizeString(lastSheetVersion.sheettitle);

    return sanitizedSheetTitle.indexOf(sanitizedSearch) === 0;
  });

  return results;
}

const getLastPubTimestamp = memoize(function memoGetLastPubTimestamp(
  sheets: SheetMap
): number {
  return sheets
    .valueSeq()
    .flatMap((s) => s.versions.map((sv) => sv.publishedtimestamp))
    .toSet()
    .sort()
    .last();
});

function filterSheets(sheets: SheetMap, sheetQuery: SheetQuery): SheetMap {
  let filteredSheets = sheets;

  if (sheetQuery.recentlyIssued) {
    const lastPublishedTimestamp = getLastPubTimestamp(sheets);

    filteredSheets = filteredSheets.filter((sheet) => {
      const sheetVersion = sheet.versions.last(new SheetVersion());

      return sheetVersion.get('publishedtimestamp') === lastPublishedTimestamp;
    });
  }

  if (sheetQuery.sheetDisciplines.size > 0) {
    filteredSheets = filteredSheets.filter((sheet) => {
      const sheetVersion = sheet.versions.last(new SheetVersion());

      return sheetQuery.sheetDisciplines.has(
        sheetVersion.getIn(['sheetDiscipline', 'designator'])
      );
    });
  }

  // Revision Comments
  if (sheetQuery.revisionComments.size > 0) {
    filteredSheets = filteredSheets.filter((sheet) => {
      const sheetVersion = sheet.versions.last(new SheetVersion());

      return sheetQuery.revisionComments.has(
        sheetVersion.get('revisioncomment')
      );
    });
  }

  return filteredSheets;
}

// TODO: Should probably memoize this, along with maybe other parts of it.
export default function querySheets(
  sheets: SheetMap,
  sheetQuery: SheetQuery
): SheetMap {
  const searchedSheets = searchSheets(sheets, sheetQuery);

  const filteredSheets = filterSheets(searchedSheets, sheetQuery);

  const sortedSheets = sortSheets(filteredSheets, sheetQuery);

  return sortedSheets;
}
